import Box from '@mui/material/Box';
import { Globe } from "lucide-react";
import { Select, MenuItem } from '@mui/material';
import { useState, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import { useRouter } from 'next/router';

interface LanguageOption {
  internalName: string;
  displayedName: string;
  locale: string;
}

export default function CustomSelect() {
  const router = useRouter();
  const [isRootHovered, setIsRootHovered] = useState(false);
  const [value, setValue] = useState("EN");

  const options: LanguageOption[] = [
    {
      "internalName": "EN",
      "displayedName": "🇬🇧 \u00A0\u00A0 English",
      "locale": "en"
    },
    {
      "internalName": "PT",
      "displayedName": "🇵🇹 \u00A0\u00A0 Português",
      "locale": "pt"
    },
    {
      "internalName": "BR",
      "displayedName": "🇧🇷 \u00A0\u00A0 Português BR",
      "locale": "br"
    }
  ];

  // Set initial value based on current locale
  useEffect(() => {
    if (router.locale === 'en') {
      setValue("EN");
    } else if (router.locale === 'pt') {
      setValue("PT");
    } else if (router.locale === 'br') {
      setValue("BR");
    }
  }, [router.locale]);

  const handleOptionChange = (selectedValue: string) => {
    setValue(selectedValue);
    const selectedOption = options.find((opt: any) => opt.internalName === selectedValue);
    if (selectedOption) {
      // Change locale using Next.js router
      router.push(router.pathname, router.asPath, { locale: selectedOption.locale });
    }
  };

  return (
    <div
      onMouseEnter={() => setIsRootHovered(true)}
      onMouseLeave={() => setIsRootHovered(false)}
      style={{
        marginTop: "2px"
      }}>
      <FormControl fullWidth>
        <Select
          labelId="outlined-basic-label"
          id="outlined-basic"
          value={value}
          label=""
          onChange={(e: any) => handleOptionChange(e.target.value)}
          renderValue={(selectedValue: string) => {
            // Find the full option object from the selected value
            const selectedOption = options.find(opt => opt.internalName === selectedValue);
            const displayName = selectedOption ? selectedOption.internalName : '';

            return (
              // Use a Box for easy flex alignment
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Globe className="w-4 h-4" />
                {displayName}
              </Box>
            );
          }}
          sx={{
            color: "rgb(188,179,168)",
            borderRadius: "8px",
            fontSize: 13,
            height: "40px",
            //backgroundColor: (isRootHovered ? "rgb(31, 31, 46)" : "rgb(29, 29, 42)"),
            backgroundColor: "rgb(31, 31, 46)",
            '& fieldset': { border: 'none' },
            '.MuiSelect-select': {
              padding: '0px 12px', // top/bottom, left/right
            },
            '& .MuiOutlinedInput-input': {
            },
            '.MuiInputBase-root': { minHeight: 'unset' },
            "& .MuiSvgIcon-root": {
              color: "rgb(188,179,168)",
              marginRight: "-15px",
              padding: "0px",
              margin: "0px",
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "rgb(31, 31, 46)",
                borderRadius: "8px",
                mt: -1.0
              },
            },
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            transformOrigin: { vertical: 'bottom', horizontal: 'center' },
          }}>
          {options.map((option: LanguageOption, index: number) => (
            <MenuItem
              key={index}
              value={option.internalName}
              sx={{
                fontSize: 14,
                fontFamily: "GT Walsheim Pro",
                borderRadius: "6px",
                margin: "5px",
                marginTop: (index === 0 ? "-3px" : "5px"),
                marginBottom: (index === options.length - 1 ? "-3px" : "5px"),
                color: "rgb(188,179,168)",
                fontWeight: (option.internalName === value ? "bold" : ""),
                opacity: (option.internalName === value ? "1.0" : "0.6"),

                // --- THIS IS THE FIX ---
                // Change these from rgba(255,255,255,0.2) to 'red'
                "&.Mui-selected": {
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
                // --- END FIX ---

                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.03)', // non-selected hover
                },

                // Your code for keyboard focus is already correct!
                '&.Mui-focusVisible': {
                  backgroundColor: "rgba(255,255,255,0.05)",
                },

                // It's also good practice to handle the case
                // where the item is BOTH selected and focused
                "&.Mui-selected.Mui-focusVisible": {
                  backgroundColor: "rgba(255,255,255,0.05)",
                }
              }}>
              {option.displayedName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}